// The insurer approval gate (docs/plans/phase2-private-insurance.md §3, §5.2): whether a
// service on an insurer visit may go ahead. Which services need approval is scheme
// configuration (`preauth_required` benefit rules); the approvals themselves are recorded
// by hand from the insurer's answer (phone, email, portal).

import type { ServiceItemCategory } from '../catalogue.ts';
import type { Money } from '../money.ts';
import type { ApprovalGateAnswer } from '../sha/preauth-gate.ts';
import type { BenefitRule } from './benefits.ts';
import { selectBenefitRules } from './benefits.ts';

/**
 * Whether a charge needs the insurer's approval: an active `preauth_required` rule for its
 * category whose threshold the charge total reaches. A charge with no price yet (total
 * null) needs it whenever a rule for its category exists, rather than guessing the amount.
 */
export function preauthRequired(
  rules: readonly BenefitRule[],
  charge: { readonly category: ServiceItemCategory; readonly total: Money | null },
): boolean {
  return selectBenefitRules(rules, { kind: 'preauth_required', category: charge.category }).some(
    (rule) => charge.total === null || charge.total.cents >= rule.amount.cents,
  );
}

export const INSURER_PREAUTH_STATES = ['requested', 'approved', 'declined', 'cancelled'] as const;
export type InsurerPreauthState = (typeof INSURER_PREAUTH_STATES)[number];

export interface InsurerPreauthFacts {
  readonly id: string;
  readonly state: InsurerPreauthState;
  readonly requestedAt: Date;
  /** The order or prescription line it was asked for; null for an item or the whole visit. */
  readonly sourceType: string | null;
  readonly sourceId: string | null;
  /** Null: the whole visit. */
  readonly serviceItemId: string | null;
  /** Exclusive. Null: no end given. */
  readonly validUntil: Date | null;
}

export const INSURER_GATE_STATES = [
  'not_required',
  'needs_request',
  'waiting',
  'approved',
  'declined',
  'expired',
] as const;
export type InsurerGateState = (typeof INSURER_GATE_STATES)[number];

function newestFirst(a: InsurerPreauthFacts, b: InsurerPreauthFacts): number {
  const byTime = b.requestedAt.getTime() - a.requestedAt.getTime();
  if (byTime !== 0) return byTime;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/**
 * Where a service stands. The pre-auths that concern it are, in order of preference, those
 * for this exact order, then those for its item on the visit, then whole-visit ones; the
 * newest one that wasn't cancelled decides (ties by ID, so reads never flip). An approval
 * counts until `validUntil`, exclusive.
 */
export function insurerPreauthGate(input: {
  readonly required: boolean;
  readonly source: { readonly type: string; readonly id: string };
  readonly serviceItemId: string;
  readonly preauths: readonly InsurerPreauthFacts[];
  readonly at: Date;
}): InsurerGateState {
  if (!input.required) return 'not_required';
  const live = input.preauths.filter((p) => p.state !== 'cancelled');
  const tiers = [
    live.filter((p) => p.sourceType === input.source.type && p.sourceId === input.source.id),
    live.filter((p) => p.sourceId === null && p.serviceItemId === input.serviceItemId),
    live.filter((p) => p.sourceId === null && p.serviceItemId === null),
  ];
  const deciding = tiers.find((t) => t.length > 0)?.sort(newestFirst)[0];
  if (deciding === undefined) return 'needs_request';
  switch (deciding.state) {
    case 'requested':
      return 'waiting';
    case 'declined':
      return 'declined';
    case 'approved':
      return deciding.validUntil !== null && input.at.getTime() >= deciding.validUntil.getTime()
        ? 'expired'
        : 'approved';
    case 'cancelled':
      return 'needs_request';
  }
}

/** The answer the lab, orders and pharmacy gates share (as SHA's `toApprovalAnswer`). */
export function insurerGateAnswer(state: InsurerGateState): ApprovalGateAnswer {
  switch (state) {
    case 'not_required':
      return 'not_applicable';
    case 'approved':
      return 'approved';
    case 'declined':
      return 'declined';
    case 'waiting':
    case 'needs_request':
    case 'expired':
      return 'waiting';
  }
}

/** Words for each state, for tags and messages. */
export const INSURER_GATE_WORDS: Record<InsurerGateState, string> = {
  not_required: 'No approval needed',
  needs_request: 'Needs the insurer’s approval',
  waiting: 'Waiting for the insurer',
  approved: 'Approved by the insurer',
  declined: 'Declined by the insurer',
  expired: 'Approval expired',
};
